/**
 * Item 4 (toast redesign): one shared entry point for every toast in the
 * app - severity styling (tinted background, darker border/text, icon) and
 * timing are decided exactly once here, not re-styled ad hoc at each call
 * site. Placement (top-center, offset below the sticky header) and the
 * manual-dismiss close button are configured globally on the Toaster
 * itself (components/ui/sonner.jsx / App.js) since those are page-level
 * concerns, not per-toast ones.
 *
 * Durations: ~4s for success/info (read and move on), ~6s for warning/
 * error (needs a beat longer to register before it auto-dismisses).
 *
 * Item 2 reopened (2026-07-28) - root cause of the "toast renders plain
 * white" bug: Sonner injects its own base stylesheet with
 * `[data-sonner-toast][data-styled=true]{background:var(--normal-bg);
 * border:1px solid var(--normal-border);color:var(--normal-text)}` - TWO
 * attribute selectors, which beats a single Tailwind utility class
 * (`.bg-emerald-50` etc.) on specificity alone, regardless of stylesheet
 * order. The className was always being applied (confirmed by the passing
 * classNames-assertion tests) - it just never won the cascade, so every
 * toast rendered with Sonner's own white/gray default underneath it. Fixed
 * by marking the three color-bearing utilities `!important` (Tailwind's
 * `!` prefix) so they always beat Sonner's rule regardless of selector
 * specificity - structural properties (padding, border-width/style,
 * border-radius, layout) are untouched, only background-color,
 * border-color, and text-color are forced. Tints also strengthened per
 * Ashok's ruling (-50/-200 read as near-white against a white page): -100
 * background, -600 border, -900 text - the severity must be unmistakable
 * at a glance, not just present in a color picker.
 */
import { toast as sonnerToast } from 'sonner';
import { CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';

export const TOAST_SEVERITY = {
  success: {
    duration: 4000,
    className: '!bg-emerald-100 !border-emerald-600 !text-emerald-900',
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" />,
  },
  warning: {
    duration: 6000,
    className: '!bg-amber-100 !border-amber-600 !text-amber-900',
    icon: <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0" />,
  },
  error: {
    duration: 6000,
    // Muted red/crimson, not a bright alert red - "corporate", not alarming.
    className: '!bg-red-100 !border-red-600 !text-red-900',
    icon: <AlertCircle className="w-5 h-5 text-red-800 shrink-0" />,
  },
  info: {
    duration: 4000,
    className: '!bg-blue-100 !border-blue-600 !text-blue-900',
    icon: <Info className="w-5 h-5 text-blue-700 shrink-0" />,
  },
};

const makeToast = (severity) => (message, options = {}) => {
  const { duration, className, icon } = TOAST_SEVERITY[severity];
  return sonnerToast[severity](message, {
    duration,
    icon,
    closeButton: true,
    ...options,
    // Severity className always wins over a caller-supplied override -
    // the whole point of this module is that severity styling isn't
    // decided per call site.
    className: `${className}${options.className ? ` ${options.className}` : ''}`,
  });
};

export const toastSuccess = makeToast('success');
export const toastWarning = makeToast('warning');
export const toastError = makeToast('error');
export const toastInfo = makeToast('info');
